"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.REGIONS = exports.DEFAULT_COUNTRY = exports.CountryCodeSchema = exports.CountryCode = void 0;
exports.resolveRegion = resolveRegion;
exports.resolveRegionSafe = resolveRegionSafe;
const zod_1 = require("zod");
/**
 * Region abstraction — the single source of truth for everything that varies by
 * country. A country is DATA in the `REGIONS` map below, never a branch in call
 * sites. Adding a country = one entry here (+ its validators/accounting rows);
 * the `Record<CountryCode, RegionConfig>` type makes the compiler reject a
 * half-specified country.
 *
 * Lives in stringline-types (no Prisma import) so server, app, portal and admin
 * all read the same config. The ONLY country fact stored in the DB is
 * `Company.country`; everything here is derived live via `resolveRegion`.
 */
// ISO 3166-1 alpha-2, restricted to what we actually support. Add 'US' etc. here.
var CountryCode;
(function (CountryCode) {
    CountryCode["AU"] = "AU";
    CountryCode["NZ"] = "NZ";
})(CountryCode || (exports.CountryCode = CountryCode = {}));
// Boundary schema (onboarding/company requests validate country with this).
exports.CountryCodeSchema = zod_1.z.nativeEnum(CountryCode);
exports.DEFAULT_COUNTRY = CountryCode.AU;
// AU identifier patterns — copied verbatim from companies.validation.ts so
// server output is unchanged when it starts reading from here.
const ABN_PATTERN = /^(\d *?){11}$/;
const ACN_PATTERN = /^(\d *?){9}$/;
exports.REGIONS = {
    [CountryCode.AU]: {
        country: CountryCode.AU,
        displayName: 'Australia',
        currency: 'AUD',
        locale: 'en-AU',
        taxNoun: 'GST',
        gstRate: 0.1,
        taxIdentifierType: 'GST',
        phone: { prefix: '+61', minNationalDigits: 10, maxNationalDigits: 10 },
        address: {
            googleComponents: 'country:au',
            googleRegion: 'au',
            countryName: 'Australia',
            postcodePattern: /^\d{4}$/,
            subdivisionLabel: 'State',
            subdivisions: [
                { value: 'NSW', label: 'New South Wales' },
                { value: 'QLD', label: 'Queensland' },
                { value: 'SA', label: 'South Australia' },
                { value: 'TAS', label: 'Tasmania' },
                { value: 'VIC', label: 'Victoria' },
                { value: 'WA', label: 'Western Australia' },
            ],
        },
        identifiers: [
            { type: 'ABN', label: 'ABN', pattern: ABN_PATTERN, message: 'Invalid ABN', mask: '99 999 999 999', optional: false },
            { type: 'ACN', label: 'ACN', pattern: ACN_PATTERN, message: 'Invalid ACN', mask: '999 999 999', optional: false },
        ],
        bank: {
            fields: [
                { key: 'bankAccountName', label: 'Account Name', pattern: null },
                { key: 'bankAccountBSB', label: 'BSB', pattern: /^\d{3}-?\d{3}$/ },
                { key: 'bankAccountNumber', label: 'Account Number', pattern: null },
            ],
        },
        accounting: {
            xero: { taxType: 'OUTPUT' },
            quickBooks: { taxCodeName: 'GST' },
            myob: { standard: 'GST', zero: 'N-T' },
        },
    },
    [CountryCode.NZ]: {
        country: CountryCode.NZ,
        displayName: 'New Zealand',
        currency: 'NZD',
        locale: 'en-NZ',
        taxNoun: 'GST',
        gstRate: 0.15,
        taxIdentifierType: 'GST',
        phone: { prefix: '+64', minNationalDigits: 8, maxNationalDigits: 10 },
        address: {
            googleComponents: 'country:nz',
            googleRegion: 'nz',
            countryName: 'New Zealand',
            postcodePattern: /^\d{4}$/,
            subdivisionLabel: 'Region',
            // ISO 3166-2:NZ regional councils.
            subdivisions: [
                { value: 'NTL', label: 'Northland' },
                { value: 'AUK', label: 'Auckland' },
                { value: 'WKO', label: 'Waikato' },
                { value: 'BOP', label: 'Bay of Plenty' },
                { value: 'GIS', label: 'Gisborne' },
                { value: 'HKB', label: "Hawke's Bay" },
                { value: 'TKI', label: 'Taranaki' },
                { value: 'MWT', label: 'Manawatū-Whanganui' },
                { value: 'WGN', label: 'Wellington' },
                { value: 'TAS', label: 'Tasman' },
                { value: 'NSN', label: 'Nelson' },
                { value: 'MBH', label: 'Marlborough' },
                { value: 'WTC', label: 'West Coast' },
                { value: 'CAN', label: 'Canterbury' },
                { value: 'OTA', label: 'Otago' },
                { value: 'STL', label: 'Southland' },
            ],
        },
        // NZBN is 13 digits; NZ GST number is 8-9 digits. The onboarding dropdown +
        // NationalIdentifierType enum gain these when NZ signup is enabled (phase 3).
        identifiers: [
            { type: 'NZBN', label: 'NZ Business Number', pattern: /^\d{13}$/, message: 'NZBN must be 13 digits', mask: '9999999999999', optional: true },
            { type: 'GST', label: 'GST Number', pattern: /^\d{8,9}$/, message: 'GST number must be 8-9 digits', mask: '999999999', optional: true },
        ],
        // NZ bank format is bank-branch-account-suffix (e.g. 12-3456-0123456-00),
        // no BSB. Column reshape + PDF handling is phase 7.
        bank: {
            fields: [
                { key: 'bankAccountName', label: 'Account Name', pattern: null },
                { key: 'bankAccountNumber', label: 'Account Number', pattern: /^\d{2}-\d{4}-\d{7}-\d{2,3}$/ },
            ],
        },
        // Unverified — no NZ Xero/QB/MYOB org yet (decision B). Providers hard-block
        // the push while these are null; fill in once a real NZ org confirms them.
        accounting: {
            xero: null,
            quickBooks: null,
            myob: null,
        },
    },
};
// A Map (not a plain object) so lookups of an untrusted/arbitrary country string
// can't resolve to inherited Object.prototype members ('constructor', 'toString',
// etc.) — a plain-object bracket lookup would return those truthy functions and
// defeat the "unknown -> throw / fall back" contract below.
const regionLookup = new Map(Object.values(exports.REGIONS).map((region) => [region.country, region]));
/**
 * Strict resolve for server-authoritative money/tax paths: an unknown country
 * must throw, never silently fall back to another country's rate. Use where the
 * country comes from validated DB data (calculateGST callers, company create).
 */
function resolveRegion(country) {
    const config = regionLookup.get(country);
    if (!config) {
        throw new Error(`Unsupported region: "${country}"`);
    }
    return config;
}
/**
 * Lenient resolve for display and client-supplied boundaries: an unknown or
 * missing country degrades to the default region instead of throwing. Use where
 * a throw would crash a customer-facing page or turn a bad query param into a
 * 500 (portal display, address-lookup country param).
 */
function resolveRegionSafe(country) {
    return regionLookup.get(country ?? '') ?? exports.REGIONS[exports.DEFAULT_COUNTRY];
}
